import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Stack,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Box,
  Typography
} from "@mui/material";

// custom components
import Heading4 from "../../typography/heading-04";
import Heading6 from "../../typography/heading-06";
import PrimaryButton from "../../buttons/primary-button";

import LoaderTextModal from "../../modal/loader-text-modal";
import SuccessModal from "../../modal/success-modal";
import ErrorModal from "../../modal/error-modal";
import PricingModal from "../../modal/pricing-modal";
import ProcessingModal from "../../modal/processing-modal";
import LoaderModal from "../../modal/loader-modal";

// reducer slice
import { selectUserDetails, selectSelectedOrganization } from "../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";

// api services
import { useLockExtensionCostCalculationMutation } from "../../../features/lock-extension-cost-calculation/lockExtensionCostCalculationApiSlice";
import { useLockExtensionSubmitRequestMutation } from "../../../features/lock-extension-submit-request/lockExtensionSubmitRequestApiSlice";

// custom styles
import styles from "./index.module.scss";


  const LockExtensionRequestFormCard = ({ loanData, obLoanID, obEntityID, obUserID, goToDefaultView  }) => {

  const [lockExtensionCostCalculation, {isLoading: isGettingCostCalculation}] = useLockExtensionCostCalculationMutation();
  const [lockExtensionSubmitRequest,  {isLoading: isSubmitRequest}] = useLockExtensionSubmitRequestMutation();

  const [days, setDays] = useState(30);
  const [lien, setLien] = useState("First Lien");
  const [cost, setCost] = useState(-0.125);
  const [finalPrice, setFinalPrice] = useState(null);
  const [message, setMessage] = useState("");
  const [showLabel, setShowLabel] = useState(false);

  const [successText, setSuccessText] = useState("");
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const userDetails = useSelector(selectUserDetails);
  const selectedOrganizationName = useSelector(selectSelectedOrganization);
  const getLastName = (fullName) => {
			if (!fullName) return null;
			const parts = fullName.trim().split(" ");
			return parts[parts.length - 1];
	};
  const handleOpenSuccessModal = () => {
    setSuccessModalVisible(true);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalVisible(false);
    goToDefaultView();
  };

  const [errorText, setErrorText] = useState("");
  const [errorHeaderText, setErrorHeaderText] = useState("");
  const [errorModalVisible, setErrorModalVisible] = useState(false);

  const handleOpenErrorModal = () => {
    setErrorModalVisible(true);
  };

  const handleCloseErrorModal = () => {
    setErrorModalVisible(false);
    goToDefaultView();
  };

  // Handle only 0–30 input for days
  const handleDaysChange = (e) => {
    const value = e.target.value;

    // Allow only numbers
    if (!/^\d*$/.test(value)) return;

    // Enforce range 0–30
    const numericValue = Number(value);
    if (numericValue >= 0 && numericValue <= 30) {
      setDays(value);
    }
  };

  const calculateCost = async () => {
    if (isNaN(days) || days < 1 || days > 30) {
      handleOpenErrorModal();
      setErrorText("Please enter a value between 1 and 30 days.");
      setCost(null);
      setFinalPrice(null);
      return;
    }
    try {
      const getResponse = await lockExtensionCostCalculation({
        ob_loan_id: obLoanID,
        ob_business_channel_id: obEntityID,
        ob_originator_id: obUserID,
        lock_extension_days: days,
      }).unwrap();

      console.log("lock extension all fields:", getResponse);

      if(getResponse.status_code === 200){
        setCost(getResponse?.data?.cost);
        setFinalPrice(getResponse?.data?.price);
        setShowLabel(true);
      }
      else{
        setCost(null);
        setFinalPrice(null);
        setErrorHeaderText("Loan File in Use");
        setErrorText(getResponse.data.errors || "Error calculating cost. Please try again.");
        handleOpenErrorModal();
      }
    
      return;
    } catch (error) {
      setCost(null);
      setFinalPrice(null);
     if (error?.status === 409){
        setErrorHeaderText("Loan File in Use");
        setErrorText(
          "Please try again later as the loan is currently being accessed by the Lock Desk. If you continue to receive this message for more than 1 hour, please contact your Account Manager for assistance."
        );
      }
      else if (error?.status === 400){
        setErrorHeaderText("Pricing Policy Issue");
        setErrorText(
          error.data?.data?.errors
        );
      }
      else{
        setErrorHeaderText("Loan File in Use");
        setErrorText("Please try again later as the loan is currently being accessed by the Lock Desk. If you continue to receive this message for more than 1 hour, please contact your Account Manager for assistance.");
        console.error("Error submitting request:", error.data?.data?.errors);
      }
      handleOpenErrorModal();
      return;
    }
  };

  const submitRequest = async () => {

    try {
      const lastName = getLastName(loanData?.titleHolderName1);
      const getResponse = await lockExtensionSubmitRequest({
        ob_loan_id: obLoanID,
        ob_business_channel_id: obEntityID,
        ob_originator_id: obUserID,
        lock_extension_days: days,
        loanguid: loanData?.encompassId,
        borrower_last_name: lastName,
        submitted_by: userDetails.first_name + " " + userDetails.last_name,
        company_name: selectedOrganizationName,
        email: userDetails.email,
        encompass_loan_number: loanData.loanNumber
      }).unwrap();

      
      if(getResponse.status_code === 200){
          // Lock Extension Submit Request
          handleOpenSuccessModal();
          setSuccessText("Your Lock Extension Request has been submitted successfully.");
      }
      else{
        handleOpenErrorModal();
        setErrorHeaderText("Loan File in Use");
        setErrorText("Please try again later as the loan is currently being accessed by the Lock Desk. If you continue to receive this message for more than 1 hour, please contact your Account Manager for assistance.");
        return;
      }
    } catch (error) {
      if (error?.status === 409){
        setErrorHeaderText("Loan File in Use");
        setErrorText(
          "Please try again later as the loan is currently being accessed by the Lock Desk. If you continue to receive this message for more than 1 hour, please contact your Account Manager for assistance."
        );
      }
      else if (error?.status === 400){
        setErrorHeaderText("Pricing Policy Issue");
        setErrorText(
          error.data?.data?.errors
        );
      }
      else{
        setErrorHeaderText("Loan File in Use");
        setErrorText("Please try again later as the loan is currently being accessed by the Lock Desk. If you continue to receive this message for more than 1 hour, please contact your Account Manager for assistance.");
        console.error("Error submitting request:", error.data?.data?.errors);
      }
      handleOpenErrorModal();
      return;
    }
  };

  return (
    <Stack direction="column" alignItems="flex-start" className={styles.cardContainer}>
      <Grid item className={styles.detailContainer}>
        <Stack direction="column" className={styles.detailDiv} spacing={2}>
          <Heading4 text={"Lock Extension Request"} />

          {/* Days Input */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 160, flexShrink: 0 }}>
              <Heading6
                text="Enter lock extension days:"
                fontType="semibold"
                style={{ whiteSpace: "nowrap", minWidth: 180 }}
              />
            </Box>

            <TextField
              type="text"
              value={days}
              onChange={handleDaysChange}
              placeholder="Enter days"
              size="small"
              sx={{
                width: "120px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  height: "30px",
                  "& fieldset": {
                    border: "3px solid #2d6166",
                  },
                  "&:hover fieldset": {
                    borderColor: "#2d6166",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2d6166",
                    borderWidth: "3px",
                  },
                  "&.Mui-disabled fieldset": {
                    borderColor: "#2d6166",
                  },
                },
                "& input": {
                  padding: "4px 8px",
                  fontSize: "0.875rem",
                  textAlign: "center",
                },
              }}
            />
          </Stack>

          {/* Lien Selection */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 160, flexShrink: 0 }}>
              <Heading6
                text="Select Lien:"
                fontType="semibold"
                style={{ whiteSpace: "nowrap", display: "inline-block" }}
              />
            </Box>
            <FormControl
              size="small"
              sx={{
                width: "120px",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "3px solid #2d6166",
                  borderRadius: "6px",
                },
              }}
            >
              <Select
                value={lien}
                onChange={(e) => setLien(e.target.value)}
                displayEmpty
                disabled
                sx={{
                  height: "35px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.875rem",
                  padding: "0 8px",
                  border: "3px solid #2d6166",
                  borderRadius: "6px",
                  backgroundColor: "#c3c2c1",
                }}
              >
                <MenuItem value="First Lien">First Lien</MenuItem>
                <MenuItem value="Second Lien">Second Lien</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Buttons */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flexShrink: 0 }}>
              <PrimaryButton
                text="Calculate Cost"
                extraClass={`${styles.buttonViewStyle}`}
                onClick={calculateCost}
              />

            </Box>
            {showLabel && (
              <Typography variant="body1" style={{ marginTop: "10px", fontWeight: "bold" }}>
                Your request will cost <span style={{ color: "red" }}>{cost}</span>. Final Price after applying the cost is {finalPrice}.
              </Typography>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flexShrink: 0 }}>
              <PrimaryButton
                text="Submit Request"
                extraClass={`${styles.buttonViewStyle}`}
                onClick={submitRequest}
              />
            </Box>
          </Stack>

          {/* Message */}
          {message && (
            <Box sx={{ mt: 1, color: "black", fontSize: "0.875rem" }}>{message}</Box>
          )}
        </Stack>
      </Grid>
      {/* custom modals */}
			<LoaderTextModal
				open={
					isGettingCostCalculation
				}
				loadingTextLine1={"Fetching Calculated Cost"}
				loadingTextLine2={"This will take a few minutes"}
			/>
      <LoaderTextModal
				open={
					isSubmitRequest
				}
				loadingTextLine1={"Submitting Lock Extension Request."}
				loadingTextLine2={"This will take a few minutes"}
			/>
      <SuccessModal
        open={successModalVisible}
        handleClose={handleCloseSuccessModal}
        text={successText}
      />
      <ErrorModal
        open={errorModalVisible}
        text={errorText}
        title={errorHeaderText}
        handleClose={handleCloseErrorModal}
      />
    </Stack>

  );
};

export default LockExtensionRequestFormCard;
